import solrResults from "../data/solr/taxonomy/commentaryTypes.json";
import { defaultLocale } from "~/i18n/locales";
import type { Dict } from "~/types";
import type { Locale } from "vue-i18n";

export type CommentaryType = {
  /**
   * Name, keyed by language code.
   */
  name: Dict<string>;

  /**
   * Description, keyed by language code.
   */
  description: Dict<string>;

  /**
   * Commentary type names used in URLs, keyed by language code.
   * Only in English is this the same as the `machineName`.
   */
  urlNames: Dict<string>;

  /**
   * Weight, used for sorting commentary types in the UI.
   */
  weight: number;

  /**
   * Whether to sort groups so that comments about fewer verses precede comments about more verses.
   */
  sortGroupsNarrow: boolean;

  /**
   * Machine name of the commentary type.
   * Only in English is this the same as the URL name.
   */
  machineName: string;

  /**
   * Universally unique identifier.
   */
  uuid: string;
};

export const unknownCommentaryType: CommentaryType = {
  name: { en: "Unknown", fr: "Inconnu", id: "Tidak dikenal" },
  description: {
    en: "Unknown commentary type",
    fr: "Type de commentaire inconnu",
    id: "Jenis komentar tidak dikenal",
  },
  weight: 65535,
  sortGroupsNarrow: true,
  machineName: "unknown",
  urlNames: { en: "unknown", fr: "inconnu", id: "tidak-dikenal" },
  uuid: "ed719aa1-f361-46d0-a874-a5a1f76e2d1d",
};

export const knownCommentaryTypes: CommentaryType[] =
  solrResults.grouped.ss_uuid.groups
    .map((group) => {
      const name: Dict<string> = {};
      const description: Dict<string> = {};
      const urlNames: Dict<string> = {};

      group.doclist.docs.forEach((doc) => {
        urlNames[doc.ss_search_api_language] = doc.ss_url_name;
        switch (doc.ss_search_api_language) {
          case "en":
            name.en = doc.tm_X3b_en_name?.[0] ?? "";
            description.en = doc.zs_description_plain[0] ?? "";
            break;
          case "fr":
            name.fr = doc.tm_X3b_fr_name?.[0] ?? "";
            description.fr = doc.zs_description_plain[0] ?? "";
            break;
          case "id":
            name.id = doc.tm_X3b_id_name?.[0] ?? "";
            description.id = doc.zs_description_plain[0] ?? "";
            break;
        }
      });

      const doc = group.doclist.docs[0];
      if (!doc) {
        throw new Error(`No doc for commentary type ${group.groupValue}`);
      }

      return {
        name,
        description,
        weight: doc?.its_weight,
        sortGroupsNarrow: doc?.bs_sort_groups_narrow,
        machineName: doc?.ss_machine_name,
        urlNames,
        uuid: group.groupValue,
      };
    })
    .sort((a, b) => a.weight - b.weight);

/**
 * Get a commentary type from its machine name. Return the unknown type if it's not available.
 */
export function getCommentaryTypeOrFallback(
  machineName: string,
): CommentaryType {
  return findCommentaryTypeByMachineName(machineName) || unknownCommentaryType;
}

/**
 * Get a commentary type from its URL name. Return the unknown type if it's not available.
 */
export function getCommentaryTypeByUrlNameOrFallback(
  urlName: string,
  locale: Locale,
): CommentaryType {
  return findCommentaryTypeByUrlName(urlName, locale) || unknownCommentaryType;
}

/**
 * Get a commentary type from a route parameter.
 *
 * The canonical route parameter is the localized URL name, but stable machine
 * names are accepted for compatibility and canonicalized by route middleware.
 */
export function getCommentaryTypeByRouteParamOrFallback(
  routeParam: string,
  locale: Locale,
): CommentaryType {
  return (
    findCommentaryTypeByUrlName(routeParam, locale) ||
    findCommentaryTypeByMachineName(routeParam) ||
    unknownCommentaryType
  );
}

/**
 * Get the URL name of a commentary type for a locale.
 */
export function getCommentaryTypeUrlName(
  type: CommentaryType,
  locale: Locale,
): string {
  return (
    type.urlNames[locale] ?? type.urlNames[defaultLocale] ?? type.machineName
  );
}

/**
 * Get a commentary type from its machine name. Throw an error if it's not available.
 */
export function requireCommentaryType(machineName: string): CommentaryType {
  const type = findCommentaryTypeByMachineName(machineName);
  if (!type) {
    throw new Error(`Unknown commentary type: ${machineName}`);
  }
  return type;
}

function findCommentaryTypeByMachineName(machineName: string) {
  return knownCommentaryTypes.find((t) => t.machineName === machineName);
}

function findCommentaryTypeByUrlName(urlName: string, locale: Locale) {
  return knownCommentaryTypes.find(
    (t) => getCommentaryTypeUrlName(t, locale) === urlName,
  );
}

export function getValidCommentaryTypeUrlNames(locale: Locale): string[] {
  return knownCommentaryTypes.map((type) =>
    getCommentaryTypeUrlName(type, locale),
  );
}

export function getValidCommentaryTypeRouteParams(locale: Locale): string[] {
  return [
    ...new Set([
      ...getValidCommentaryTypeUrlNames(locale),
      ...knownCommentaryTypes.map((type) => type.machineName),
    ]),
  ];
}
